import { Navigate, useLocation, Link } from "react-router-dom";
import { ShieldOff, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Skel, EmptyState } from "@/components/app/Common";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/app/Sidebar";

export const FullPageLoader = ({ boot = "checking", onRetry }) => (
  <div className="flex min-h-screen bg-canvas" data-testid="app-skeleton" data-boot={boot} aria-busy="true">
    <div className="w-[68px] shrink-0 space-y-6 border-r border-line bg-white p-3 md:w-[240px] md:p-4" data-testid="app-skeleton-sidebar">
      <span className="flex items-center gap-2"><LogoMark /><span className="tl-wordmark hidden font-display text-lg font-semibold md:inline">Onboard</span></span>
      {[0, 1, 2, 3, 4, 5].map((i) => <Skel key={i} className="h-7 w-full" />)}
    </div>
    <div className="min-w-0 flex-1" data-testid="page-loader">
      <div className="flex h-16 items-center gap-4 border-b border-line bg-white px-6" data-testid="app-skeleton-topbar"><Skel className="h-9 w-80 max-w-full" /><Skel className="ml-auto h-9 w-9 rounded-full" /></div>
      <div className="space-y-5 p-8">
        {boot === "waking" && <p className="flex items-center gap-2 text-sm font-medium text-brand" data-testid="waking-message" aria-live="polite"><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Waking things up… reconnecting automatically.</p>}
        {boot === "failed" && (
          <div className="flex flex-wrap items-center gap-3 rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-4 py-3 text-sm text-caution" data-testid="boot-failed" role="alert">
            <span>We can't reach Onboard right now. We'll keep trying in the background.</span>
            <Button size="sm" variant="outline" onClick={onRetry} data-testid="boot-retry-btn">Try again</Button>
          </div>
        )}
        <Skel className="h-8 w-56" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="panel space-y-3 p-5"><Skel className="h-3 w-24" /><Skel className="h-8 w-16" /></div>)}</div>
        <div className="panel space-y-3 p-5">{[0, 1, 2, 3, 4].map((i) => <Skel key={i} className="h-10 w-full" />)}</div>
      </div>
    </div>
  </div>
);

export function RequireAuth({ children }) {
  const { user, boot, retry } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullPageLoader boot={boot} onRetry={retry} />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

export const NoAccess = () => (
  <EmptyState
    testId="no-access"
    icon={ShieldOff}
    title="You don't have access to this page"
    text="Your role doesn't include this area. If you think you need it, contact your firm administrator."
    action={<Button asChild variant="outline"><Link to="/app">Back to dashboard</Link></Button>}
  />
);

export function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <NoAccess />;
  return children;
}
